import { DeeplyReadonly, Filter, Query } from '@cubejs-client/core';
import { useCubeQuery } from '@cubejs-client/react';
import { ArrowDropDown, DescriptionOutlined } from '@mui/icons-material';
import { Box, Button, Dialog, DialogContent, Link } from '@mui/material';
import { Operator } from '@procureai/query-builder';
import {
  formatNumber,
  LinearProgressChart,
  LinearProgressList,
  LinearProgressListItem,
  LinearProgressListSubheader,
  NumberFormatPreset,
  Widget,
  WidgetContent,
  WidgetFooter,
  WidgetHeader,
  WidgetIcon,
  useScrollIntoView,
} from '@procureai/ui-mui';
import { isEmpty, isNil } from 'ramda';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { BaseDomainType, I18nNamespace, SupplierQueryField } from '~/common/types';
import { ButtonSelect } from '~/components/ButtonSelect/ButtonSelect';
import { CircularProgressCentered } from '~/components/CircularProgressCentered/CircularProgressCentered';
import { EncodedQuery } from '~/components/EncodedQuery/EncodedQuery';
import { useModuleContext } from '~/components/ModuleContextProvider';
import { PlaceholderWidget } from '~/components/PlaceholderWidget/PlaceholderWidget';
import { VirtualList, VirtualListProps } from '~/components/VirtualList/VirtualList';
import { useOpenClose } from '~/hooks';
import { RouteId } from '~/routes';
import { lastYearDateRange } from '~/util/cube';

const MAX_ITEMS = 3;

export const CommoditySupplierListWidget = () => {
  const { entityId } = useModuleContext();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'CommoditySupplierListWidget' });
  const [widgetRef, active] = useScrollIntoView({ hash: 'commodity-supplier-list' });

  const [dialogProps, dialogOpen] = useOpenClose();
  const [option, setOption] = useState(0);

  const tooltipFormatter = formatNumber(option === 0 ? NumberFormatPreset.CURRENCY : NumberFormatPreset.INTEGER);

  const optionItems = [
    {
      label: t('volume'),
      value: 0,
    },
    {
      label: t('quantity'),
      value: 1,
    },
  ];

  const cube = 'Commodity';

  const filters: Filter[] = [
    {
      member: `${cube}.commodity_id`,
      operator: 'equals',
      values: [entityId],
    },
  ];

  const queryVolume: DeeplyReadonly<Query> = {
    measures: [`${cube}.monthly_order_volume`],
    dimensions: [
      `${cube}.supplier_count`,
      `${cube}.total_monthly_order_volume`,
      `${cube}.name`,
      `${cube}.sourceReference`,
      `${cube}.supplier_id`,
    ],
    timeDimensions: [
      {
        dimension: `${cube}.creation_date`,
        granularity: 'month',
        dateRange: lastYearDateRange,
      },
    ],
    order: {
      [`${cube}.monthly_order_volume`]: 'desc',
    },
    filters,
  };
  const queryQuantity: DeeplyReadonly<Query> = {
    measures: [`${cube}.monthly_order_quantity`],
    dimensions: [
      `${cube}.supplier_count`,
      `${cube}.total_monthly_order_quantity`,
      `${cube}.name`,
      `${cube}.sourceReference`,
      `${cube}.supplier_id`,
    ],
    timeDimensions: [
      {
        dimension: `${cube}.creation_date`,
        granularity: 'month',
        dateRange: lastYearDateRange,
      },
    ],
    order: {
      [`${cube}.monthly_order_quantity`]: 'desc',
    },
    filters,
  };
  const query = option === 0 ? queryVolume : queryQuantity;
  const queryValueByVolume = useCubeQuery({ ...queryVolume, limit: MAX_ITEMS });
  const queryValueByQuantity = useCubeQuery({ ...queryQuantity, limit: MAX_ITEMS });

  const nodesVolume = queryValueByVolume.resultSet?.rawData()?.map((segment) => ({
    id: segment[`${cube}.sourceReference`],
    name: segment[`${cube}.name`],
    supplierCount: segment[`${cube}.supplier_count`],
    totalVolume: Number(segment[`${cube}.total_monthly_order_volume`]) || 0,
    orderVolume: Number(segment[`${cube}.monthly_order_volume`]) || 0,
    supplierId: segment[`${cube}.supplier_id`],
  }));
  const nodesQuantity = queryValueByQuantity.resultSet?.rawData()?.map((segment) => ({
    id: segment[`${cube}.sourceReference`],
    name: segment[`${cube}.name`],
    supplierCount: segment[`${cube}.supplier_count`],
    totalQuantity: Number(segment[`${cube}.total_monthly_order_quantity`]) || 0,
    orderQuantity: Number(segment[`${cube}.monthly_order_quantity`]) || 0,
    supplierId: segment[`${cube}.supplier_id`],
  }));
  const nodes = option === 0 ? nodesVolume : nodesQuantity;

  const isNodesEmpty = isEmpty(nodes) || isNil(nodes);
  const isLoading = queryValueByVolume.isLoading || queryValueByQuantity.isLoading;

  const supplierIds = nodes?.map((item) => item.supplierId);
  const totalCount = nodes?.[0]?.supplierCount;
  const hasItems = !!totalCount;
  const hasMore = (totalCount || 0) > MAX_ITEMS;
  const showInBaseCount = nodes ? (nodes.length < MAX_ITEMS ? nodes.length : MAX_ITEMS) : 0;

  const rules = useMemo(() => {
    return [{ field: SupplierQueryField.id, value: supplierIds, operator: Operator.INEQ }];
  }, [supplierIds]);

  const renderItem: NonNullable<VirtualListProps<any>['renderItem']> = useMemo(
    () =>
      ({ item }) => {
        const { id, supplierId, name, totalVolume, totalQuantity, orderVolume, orderQuantity } = item;
        const barMax = option === 0 ? totalVolume : totalQuantity;
        const barValue = option === 0 ? orderVolume : orderQuantity;

        return (
          <Link key={id} component={RouterLink} to={RouteId.supplierDetail(supplierId)} underline="none">
            <Box
              style={{ listStyleType: 'none' }}
              sx={{
                py: 4,
              }}
            >
              <LinearProgressListItem
                key={id}
                component="div"
                primary={name}
                primaryRight={t(!barMax ? 'value' : 'valueTotal', {
                  spent: barValue,
                  total: barMax,
                })}
                secondary={id}
              >
                {barValue !== null && barMax !== null && (
                  <LinearProgressChart value={barValue} max={barMax} tooltipFormatter={tooltipFormatter} />
                )}
              </LinearProgressListItem>
            </Box>
          </Link>
        );
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [option, t],
  );

  return (
    <>
      <Widget ref={widgetRef} active={active} data-intercom-target="CommoditySupplierListWidget">
        <WidgetHeader
          title={t('title')}
          icon={<WidgetIcon icon={<DescriptionOutlined fontSize="small" />} />}
          actionsRight={
            <ButtonSelect
              endIcon={<ArrowDropDown fontSize="small" />}
              items={optionItems}
              selected={option}
              onItemClick={setOption}
            />
          }
        />
        <WidgetContent>
          {!isNodesEmpty ? (
            <LinearProgressList
              items={nodes}
              renderItem={renderItem}
              subheader={
                hasItems ? (
                  <LinearProgressListSubheader disableGutters disableSticky>
                    {t(option === 0 ? 'listSubheaderVolume' : 'listSubheaderQuantity')}
                  </LinearProgressListSubheader>
                ) : undefined
              }
            />
          ) : isLoading ? (
            <CircularProgressCentered sx={{ position: 'relative' }} />
          ) : (
            <PlaceholderWidget />
          )}
        </WidgetContent>
        <WidgetFooter>
          <Button variant="text" disabled={!hasMore || isNodesEmpty} onClick={dialogOpen}>
            {t('showAll', { count: totalCount || 0 })}
          </Button>
          <EncodedQuery pathname={RouteId.analyticsBase(BaseDomainType.SUPPLIER)} rules={rules}>
            {(linkProps) => (
              <Button variant="text" disabled={!hasItems} {...linkProps}>
                {t('showInBase', { count: showInBaseCount })}
              </Button>
            )}
          </EncodedQuery>
        </WidgetFooter>
      </Widget>
      <Dialog
        {...dialogProps}
        sx={{
          '& .MuiDialog-paper': {
            background: 'transparent',
            boxShadow: 'none',
          },
        }}
      >
        <DialogContent>
          <Widget>
            <WidgetHeader
              title={t('title')}
              icon={<WidgetIcon icon={<DescriptionOutlined fontSize="small" />} />}
              actionsRight={
                <ButtonSelect
                  endIcon={<ArrowDropDown fontSize="small" />}
                  items={optionItems}
                  selected={option}
                  onItemClick={setOption}
                />
              }
            />
            <WidgetContent>
              <Box
                sx={{
                  marginBottom: 16,
                }}
              >
                <VirtualList
                  itemSize={76}
                  query={query}
                  queryOptions={{
                    skip: !dialogProps.open,
                    segmentMapper: (segment) => ({
                      id: segment[`${cube}.sourceReference`],
                      supplierId: segment[`${cube}.supplier_id`],
                      name: segment[`${cube}.name`],
                      supplierCount: segment[`${cube}.supplier_count`],
                      totalVolume: Number(segment[`${cube}.total_monthly_order_volume`]) || 0,
                      orderVolume: Number(segment[`${cube}.monthly_order_volume`]) || 0,
                      totalQuantity: Number(segment[`${cube}.total_monthly_order_quantity`]) || 0,
                      orderQuantity: Number(segment[`${cube}.monthly_order_quantity`]) || 0,
                    }),
                    variables: {
                      id: entityId,
                    },
                  }}
                  renderItem={renderItem}
                />
              </Box>
            </WidgetContent>
          </Widget>
        </DialogContent>
      </Dialog>
    </>
  );
};
